<script>
	/**
	 * 런처페이지 저작 화면.
	 * 설계: project-le868/launcher-page-design.md §7
	 *
	 * ⚠️ 셸 규격은 ChatBuilder·GameBuilder 와 같다 — 상단 고정 탑바(56px) 아래 .builder-shell,
	 *    좌측 사이드바(폰은 상단 가로 탭), sticky 액션 툴바, 우측 프리뷰(좁으면 드로어).
	 * ⚠️ 저장은 **탭이 만진 칸만** 보낸다(서버 modifyLauncherSettings 가 부분 저장).
	 *    전체를 매번 보내면 한 탭의 낡은 값이 다른 탭의 최신 저장을 덮어쓴다.
	 */
	import { onDestroy, onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Select } from "./../lib/ui";
	import { planStore } from "./store/plan-store";
	import { messaging } from "./store/messaging-store";
	import LauncherBuilderSidebar from "./launcher-builder/shell/LauncherBuilderSidebar.svelte";
	import LauncherBuilderTopTabs from "./launcher-builder/shell/LauncherBuilderTopTabs.svelte";
	import ShellContext from "./launcher-builder/ShellContext.svelte";
	import ScreenContext from "./launcher-builder/ScreenContext.svelte";
	import TileContext from "./launcher-builder/TileContext.svelte";
	import RecipeContext from "./launcher-builder/RecipeContext.svelte";
	import IdentityContext from "./launcher-builder/IdentityContext.svelte";
	import EconomyContext from "./launcher-builder/EconomyContext.svelte";
	import ShareContext from "./launcher-builder/ShareContext.svelte";
	import OpsReportContext from "./launcher-builder/OpsReportContext.svelte";

	export let currentRoute;

	let botId;
	let isLoaded = false;
	let saveStatus = "idle"; // idle | pending(치는 중 — 포커스가 빠지면 저장) | saving | saved
	let errorMessage = "";
	let previewKey = 0;
	// 프리뷰 셸이 가리키는 **런처 번들 버전**. ensureLauncherShell 응답에서 받아 iframe URL 에 싣는다.
	// null = 아직 못 받음 → iframe 을 띄우지 않는다(먼저 띄우면 버전 도착 시 두 번 로드된다).
	let shellVersion = null;

	/**
	 * ⭐ 프리뷰에 "이 화면 보여줘"라고 알린다.
	 *
	 * 온보딩·로그인·안내 화면은 **조건이 맞아야** 뜬다(로그인 여부·완료 여부). 저작 중에는 그 조건을
	 * 만들 수 없어 **만드는 화면을 볼 수가 없었다** — 그래서 직접 가리킨다.
	 * ⚠️ 메시지 이름은 런타임 LAUNCHER/src/config.ts 의 PREVIEW_FOCUS 와 같아야 한다.
	 */
	let previewFocus = "";
	let previewWindow = null;
	// ⚠️ 메인(홈) 화면은 **빈 문자열 = focus 없음**이다(런타임은 가리킨 게 없으면 홈을 그린다).
	//    'home' 을 따로 보내면 같은 화면에 값이 두 개가 생겨 [화면] 탭의 선택 표시가 어긋난다.
	const focusOf = (v) => {
		if (!v) return null;
		if (v.startsWith("ob:")) return { kind: "onboarding", index: Number(v.slice(3)) || 0 };
		// ⭐ [레시피] 탭의 결과 전 화면. `nonce` 가 오르면 런타임이 그 화면을 처음부터 다시 세운다
		//    (연출을 고르면서 보려고). ⚠️ 런타임 types.ts PreviewFocus 와 같은 모양이어야 한다.
		// ⭐ [레시피] 탭의 **단계**(안내·만드는 중·결과 전·결과). `st:<레시피 id>[:<단계>]` — 단계가 없으면 안내부터.
		//    ⚠️ 런타임 types.ts PreviewFocus 의 recipeStep 과 같은 모양이어야 한다.
		if (v.startsWith("st:")) {
			const [, recipeId, step = "intro"] = v.split(":");
			return { kind: "recipeStep", recipeId, step, nonce: previewNonce };
		}
		// ⭐ 경품 이벤트 화면. `evt:<레시피 id>[:<팝업>[:<버튼 상태>]]` — 팝업 info|win|lose,
		//    버튼 상태 done|won(꺼진 버튼 문구를 보면서 쓰려고 — 팝업 없이 상태만이면 `evt:<id>::won`).
		// ⭐ ⑤ 결과 버튼의 '오늘은 이미 받았어요' 창. `done:<레시피 id>[:all]` — all = 이벤트도 다 했을 때 모양.
		if (v.startsWith("done:")) {
			const [, recipeId, all = ""] = v.split(":");
			return { kind: "recipeDone", recipeId, all: all === "all", nonce: previewNonce };
		}
		if (v.startsWith("evt:")) {
			const [, recipeId, popup = "", state = ""] = v.split(":");
			return { kind: "recipeEvent", recipeId, popup, state, nonce: previewNonce };
		}
		return { kind: v };
	};
	/** 이 focus 가 어느 탭에서 만든 것인지. 그 탭을 떠나면 프리뷰를 홈으로 돌린다. */
	const tabOfFocus = (v) => (v.startsWith("st:") || v.startsWith("evt:") || v.startsWith("done:") ? "recipe" : "screen");
	/** `evt:<id>:<팝업>` → `evt:<id>` (드롭다운·유효성 검사는 팝업과 무관하게 이벤트 단위로 본다). */
	const eventFocusBase = (v) => v.split(":").slice(0, 2).join(":");
	/**
	 * 결과 전 화면 **다시 재생** 카운터. 설정 갱신(글자 한 자마다 온다)에도 같은 값이 실려 가므로,
	 * 이 값이 오를 때만 연출이 처음으로 돌아간다 — 말풍선을 치는 동안 연출이 계속 리셋되지 않게.
	 */
	let previewNonce = 0;
	// ⚠️ iframe 이 아직 안 떴으면 보내도 받을 사람이 없다 → 로드된 뒤(ready)와 바뀔 때마다 보낸다.
	const sendFocus = () => {
		const payload = { type: "launcher-preview-focus", focus: focusOf(previewFocus) };
		// ⚠️ 프리뷰 배선은 창을 건너다녀서 **어디서 끊겼는지 보이지 않으면** 고칠 수가 없다.
		//    저작 화면에서만 찍히므로 실사용자에겐 영향이 없다.
		console.log("[launcher:builder] sendFocus", {
			hasWindow: !!previewWindow,
			closed: previewWindow?.closed,
			payload,
		});
		if (!previewWindow) {
			console.warn("[launcher:builder] 프리뷰 창을 아직 못 잡았다 — iframe 이 ready 를 안 보냈다");
			return;
		}
		previewWindow.postMessage(payload, window.location.origin);
	};

	/**
	 * 프리뷰에 띄울 화면을 바꾼다.
	 *
	 * ⚠️⚠️ **반응문(`$: previewFocus, sendFocus()`)으로 두지 말 것.** 시퀀스 표현식은 의존성 추적이
	 *    불안정해 값만 바뀌고 전송이 안 되는 일이 생긴다(실제로 셀렉트가 먹통이었다).
	 *    바꾸는 자리에서 **직접 보낸다** — 흐름이 한눈에 보이고 조용히 끊길 자리가 없다.
	 * ⚠️ 같은 값이면 보내지 않는다 — 입력 한 글자마다 focusin 이 올라와 메시지가 쏟아진다.
	 */
	const setPreviewFocus = (value) => {
		const next = value || "";
		if (next === previewFocus) return;
		previewFocus = next;
		sendFocus();
		// 좁은 화면에선 프리뷰가 닫혀 있다 — 보여주려고 바꿨는데 안 보이면 의미가 없다.
		if (next && isNarrow) previewOpen = true;
	};

	// 고를 수 있는 화면. 저작한 만큼만 나온다(없는 장을 가리킬 수 없게).
	$: focusChoices = [
		{ value: "", label: "메인 화면" },
		...(settings.shell?.screens?.onboarding || []).map((_, i) => ({
			value: `ob:${i}`,
			label: `온보딩 ${i + 1}장`,
		})),
		{ value: "login", label: "로그인 화면" },
		{ value: "profileIntro", label: "개인정보 안내" },
		// 레시피는 **레시피 단위**로 고른다(단계는 [레시피] 탭의 단계 탭이 정한다).
		...(settings.recipes || [])
			.filter((r) => r?.kind !== "event")
			.map((r) => ({ value: `st:${r.id}`, label: `레시피 — ${r.name || "(이름 없는 레시피)"}` })),
		...(settings.recipes || [])
			.filter((r) => r?.kind === "event")
			.map((r) => ({ value: `evt:${r.id}`, label: `경품 이벤트 — ${r.name || "(이름 없는 이벤트)"}` })),
	];
	// ⚠️ 단계·팝업까지 가리킨 값(`st:id:result`, `evt:id:win`)은 드롭다운에 없다 → 레시피 단위로 접어
	//    보여준다(안 그러면 빈칸).
	// '오늘은 이미 받았어요' 창(done:)은 그 레시피(st:)로 접어 보여준다.
	$: focusSelectValue = previewFocus.startsWith("done:")
		? `st:${previewFocus.split(":")[1]}`
		: previewFocus.startsWith("evt:") || previewFocus.startsWith("st:")
			? eventFocusBase(previewFocus)
			: previewFocus;
	let isNarrow = false; // ≤1023px → 프리뷰를 드로어로
	let previewOpen = true;

	let bot = { id: "", title: "", pageType: "LAUNCHER", charId: "", pageUrl: "", designateUrl: "" };
	// 게시 기준 두 날짜(BOTS). 저장할 때마다 서버가 응답 header 에 최신값을 실어보낸다
	// (Bot.js touchBotDate) → 2차 라운드트립 없이 '미반영 변경' 점을 갱신할 수 있다.
	let lastUpdateDate = null;
	let lastPostDate = null;
	// 한 번도 게시 안 했으면 false — 그때는 '미반영'이 아니라 '아직 게시 전'이라
	// 점을 띄우는 대신 첫 클릭이 바로 게시로 간다.
	$: hasUnpublished = !!lastPostDate && new Date(lastPostDate) < new Date(lastUpdateDate);

	// 저작 상태. 서버 BOT_LAUNCHER_SETTINGS 한 행과 1:1이다.
	let settings = { shell: {}, theme: {}, tiles: [], recipes: [], identity: {}, economy: {} };
	// 썸네일용 resolve 결과(저장하지 않는다). 슬롯키→URL / assetId→URL — **키 체계가 다르다.**
	let shellUrls = {};
	let iconUrls = {};
	let currencyUrl = "";
	/** 레시피 안 그림들의 assetId → URL(썸네일용). 저장하지 않는다. */
	let recipeUrls = {};
	/** 온보딩·로그인·안내 화면 그림의 assetId → URL(썸네일용). */
	let screenUrls = {};

	// 순서 = 저작 순서. 톤을 정하고(디자인=로고·테마), 화면을 만들고(화면=메인·온보딩·로그인),
	// 거기에 들어갈 것을 붙이고(타일), 누가 쓰는지를 정한다(신원).
	// ⭐ 메인(홈) 화면 저작은 [화면] 탭에 있다 — 프리뷰 화면을 가리키는 건 그 탭뿐이다.
	const tabList = [
		{ value: "design", label: "디자인" },
		{ value: "screen", label: "화면" },
		{ value: "tile", label: "타일" },
		{ value: "recipe", label: "레시피" },
		{ value: "identity", label: "신원" },
		{ value: "economy", label: "경제" },
		// 마지막 단계 = 내보내기. 저장소도 혼자 다르다(BOTS 한 행 — ShareContext 주석).
		{ value: "share", label: "공유" },
		// 저작이 아니라 **운영** — 게시본 방문·행운뽑기 숫자(제출용 캡처). 저장할 게 없어 미리보기를 접는다.
		{ value: "ops", label: "운영 지표" },
	];
	let currentTab = "design";
	$: opsTab = currentTab === "ops";
	$: currentTabLabel = tabList.find((t) => t.value === currentTab)?.label || "";

	onMount(() => {
		botId = currentRoute.namedParams.botId;
		loadAll();
		// ⚠️ loadAll 과 **나란히** 보낸다. 앞에 세우면 이 왕복이 끝날 때까지 빌더가 비어 있는다.
		PRConnect.send("/api/Bot/ensureLauncherShell", { botId }, (data) => {
			if (data.header.status === "success") shellVersion = data.result?.version || null;
		});

		// 반응형: 좁은 화면에선 프리뷰를 사이드가 아니라 오버레이 드로어로(다른 빌더와 같은 경계).
		const mql = window.matchMedia("(max-width: 1023px)");
		const apply = () => {
			isNarrow = mql.matches;
			previewOpen = !mql.matches;
		};
		apply();
		mql.addEventListener("change", apply);
		window.addEventListener("message", onPreviewMessage);
		document.addEventListener("focusout", onFocusOut, true);
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => {
			mql.removeEventListener("change", apply);
			window.removeEventListener("message", onPreviewMessage);
			document.removeEventListener("focusout", onFocusOut, true);
			window.removeEventListener("beforeunload", onBeforeUnload);
		};
	});

	// 게시 완료 통지(LauncherBuilderLayout 이 보냄): 방금 전부 게시됐으니 게시일자를 최신 수정일자로
	// 맞춰 sticky '게시하기'의 미반영 점을 즉시 끈다.
	// ⚠️ 구독 즉시 오는 **현재 값 재생**은 버린다(레이아웃 쪽 주석과 같은 이유).
	let publishMsgReplayed = false;
	const publishMsgSubsc = messaging.subscribe((message) => {
		if (!publishMsgReplayed) {
			publishMsgReplayed = true;
			return;
		}
		if (message?.target === "LauncherBuilder" && message.action === "published") {
			lastPostDate = lastUpdateDate || new Date().toISOString();
		}
	});
	onDestroy(() => publishMsgSubsc());

	const loadAll = () => {
		// ⚠️ getBotInfo 의 파라미터는 `id` 다(`botId` 가 아니다). 런처 봇에는 시나리오·버튼이 없어
		//    응답 대부분이 비지만, 프리뷰 경로에 필요한 charId 가 여기 들어 있다.
		PRConnect.send("/api/Bot/getBotInfo", { id: botId }, (data) => {
			if (data.header.status === "success") {
				bot = {
					...bot,
					...(data.result?.botData || {}),
					id: botId,
					charId: data.result?.botChar?.charId || "",
				};
				lastUpdateDate = bot.lastUpdateDate || null;
				lastPostDate = bot.lastPostDate || null;
			}
			loadSettings();
		});
	};

	const loadSettings = () => {
		PRConnect.send("/api/Bot/getLauncherConfig", { botId }, (data) => {
			if (data.header.status !== "success") {
				// 설정 행이 없으면 저장이 조용히 0건이 된다(저작자에겐 '저장했는데 안 바뀐다'로만 보인다)
				// → 화면에서 바로 알려준다. 보통 마이그레이션 미적용이 원인이다.
				errorMessage = "런처 설정을 불러오지 못했습니다. 저장이 반영되지 않을 수 있어요.";
				isLoaded = true;
				return;
			}
			const r = data.result || {};
			// ⚠️⚠️ **저작값은 `authoring` 에서 읽는다.** r.shell / r.tiles 는 런타임용이라
			//    assetId 가 URL 로 바뀌어 있고 ref(iconAssetId)는 아예 빠져 있다 — 그걸 그대로
			//    저장하면 SHELL/TILES 의 ref 가 URL 로 오염돼 자산 교체가 안 먹고 아이콘이 사라진다.
			//    (게임 GameBuilder 가 `*AssetIds` 를 쓰는 것과 같은 이유)
			const a = r.authoring || {};
			settings = {
				shell: a.shell || {},
				theme: a.theme || {},
				tiles: Array.isArray(a.tiles) ? a.tiles : [],
				recipes: Array.isArray(a.recipes) ? a.recipes : [],
				identity: a.identity || {},
				economy: a.economy || {},
			};
			// 썸네일용 URL 은 **런타임 쪽**에서 가져온다(resolve 가 거기서 끝나 있다).
			shellUrls = { ...(r.shell?.assets || {}) };
			// 재화 그림도 런타임 쪽 resolve 결과를 쓴다(저작값은 assetId, 썸네일은 URL).
			currencyUrl = r.economy?.currencyUrl || "";
			// 레시피 그림도 두 벌을 맞춰 붙인다 — 저작용엔 assetId, 런타임용엔 resolve 된 URL.
			// ⚠️ 레시피 id / 카드 id 로 짝지어야 한다(배열 순서를 믿으면 하나만 지워도 전부 어긋난다).
			const rtRecipeById = new Map((r.recipes || []).map((x) => [x.id, x]));
			const urls = {};
			for (const ar of settings.recipes) {
				const rt = rtRecipeById.get(ar.id);
				if (!rt) continue;
				// ⚠️⚠️ **배경도 짝지어야 한다.** 예전엔 `imageAssetId` 만 이어 붙여서, 저작자가 올린
				//    배경은 저장은 되는데 **빌더를 다시 열면 썸네일만 빈칸**이었다(`card.images` 가
				//    당한 것과 같은 함정 — 그쪽은 collectAssetIds, 이쪽은 이 짝짓기였다).
				const pairImg = (a, r2) => {
					if (a?.imageAssetId && r2?.imageUrl) urls[a.imageAssetId] = r2.imageUrl;
					if (a?.backgroundAssetId && r2?.backgroundUrl) urls[a.backgroundAssetId] = r2.backgroundUrl;
					if (a?.standAssetId && r2?.standUrl) urls[a.standAssetId] = r2.standUrl;
					if (a?.titleAssetId && r2?.titleUrl) urls[a.titleAssetId] = r2.titleUrl;
					// 결과 전 화면의 빛 그림.
					if (a?.lightAssetId && r2?.lightUrl) urls[a.lightAssetId] = r2.lightUrl;
				};
				pairImg(ar.intro, rt.intro);
				pairImg(ar.loading, rt.loading);
				pairImg(ar.reveal, rt.reveal);
				// ⭐ 경품 이벤트 그림들 — 저작값(assetId)과 런타임값(URL)을 **id·자리로** 짝짓는다.
				//    ⚠️ 하나라도 빠지면 저장은 되는데 빌더를 다시 열 때 그 썸네일만 빈칸이 된다.
				if (ar.kind === "event" && rt.event) {
					const ae = ar.event || {};
					const re = rt.event;
					const put = (id, url) => {
						if (id && url) urls[id] = url;
					};
					put(ae.backgroundAssetId, re.backgroundUrl);
					put(ae.titleAssetId, re.titleUrl);
					put(ae.lose?.imageAssetId, re.lose?.imageUrl);
					put(ae.draw?.machineAssetId, re.draw?.machineUrl);
					put(ae.draw?.handleAssetId, re.draw?.handleUrl);
					put(ae.draw?.floorAssetId, re.draw?.floorUrl);
					put(ae.tip?.iconAssetId, re.tip?.iconUrl);
					const rtPrize = new Map((re.prizes || []).map((p) => [p.id, p.imageUrl]));
					for (const p of ae.prizes || []) put(p.imageAssetId, rtPrize.get(p.id));
					// ⚠️ 캡슐은 런타임에서 **못 푼 것을 빼고** 내려온다 → 개수가 같을 때만 자리로 짝짓는다.
					const ac = ae.draw?.capsuleAssetIds || [];
					const rc = re.draw?.capsuleUrls || [];
					if (ac.length === rc.length) ac.forEach((id, i) => put(id, rc[i]));
				}
				pairImg(ar.result, rt.result);
				const rtCardById = new Map((rt.result?.pool || []).map((c) => [c.id, c]));
				for (const ac of ar.result?.pool || []) {
					const rc = rtCardById.get(ac.id);
					if (ac.imageAssetId && rc?.imageUrl) urls[ac.imageAssetId] = rc.imageUrl;
				}
			}
			recipeUrls = urls;
			// 화면(말 거는 화면) 그림도 두 벌을 맞춰 붙인다 — 저작용엔 assetId, 런타임용엔 URL.
			const sU = {};
			const pair = (a, r) => {
				if (a?.imageAssetId && r?.imageUrl) sU[a.imageAssetId] = r.imageUrl;
				if (a?.backgroundAssetId && r?.backgroundUrl) sU[a.backgroundAssetId] = r.backgroundUrl;
				if (a?.titleAssetId && r?.titleUrl) sU[a.titleAssetId] = r.titleUrl;
			};
			const aS = settings.shell?.screens || {};
			const rS = r.shell?.screens || {};
			(aS.onboarding || []).forEach((x, i) => pair(x, (rS.onboarding || [])[i]));
			pair(aS.login, rS.login);
			pair(aS.profileIntro, rS.profileIntro);
			screenUrls = sU;
			// assetId→URL. 타일 id 로 두 벌을 맞춰 붙인다(저작용엔 ref, 런타임용엔 URL).
			const urlByTileId = new Map((r.tiles || []).map((t) => [t.id, t.iconUrl]));
			iconUrls = Object.fromEntries(
				settings.tiles
					.filter((t) => t.iconAssetId && urlByTileId.get(t.id))
					.map((t) => [t.iconAssetId, urlByTileId.get(t.id)]),
			);
			isLoaded = true;
		});
	};

	/* ── 저장 묶기 ─────────────────────────────────────────────────────────
	 * ⭐ **글자를 칠 때마다 저장하지 않는다.** 탭들은 입력 한 글자마다 change 를 올리는데(빌더 전체가
	 *    on:input 이다), 예전엔 그걸 그대로 modifyLauncherSettings 로 보내서 "안녕하세요" 한 줄에
	 *    요청이 5번 나갔다(+ 매번 getLauncherConfig 로 프리뷰 갱신까지 — 왕복이 두 배).
	 * → 탭이 올린 값은 `pending` 에 **칸별 최신값으로 모아 두고**, 이럴 때 한 번에 보낸다:
	 *    · 글자 칸에서 **포커스가 빠질 때**(focusout — 다른 칸으로 옮기기·바깥 클릭·창 전환 다 포함)
	 *    · 글자 칸이 아닌 조작(셀렉트·스위치·그림 고르기·버튼)은 **그 자리에서 바로** — 한 번에 끝나는 조작이다
	 *    · 탭 이동 · 게시 직전 · 화면을 떠날 때
	 * ⚠️ 그래서 **프리뷰도 포커스가 빠질 때 바뀐다**(타이핑 중 글자마다 따라오지 않는다).
	 * ⚠️⚠️ 요청은 **한 줄로 세운다**(동시에 하나만). 둘이 겹쳐 나가면 서버에서 도착 순서가 뒤바뀌어
	 *    **옛 값이 새 값을 덮을 수 있다** — 칸 하나를 JSON 통째로 덮어쓰는 저장이라 그대로 날아간다.
	 *    보내는 중에 쌓인 건 그 요청이 끝나면 이어서 보낸다.
	 */
	/** 아직 안 보낸 칸들 `{ shell?, theme?, tiles?, recipes?, identity?, economy? }` — 칸마다 최신값 하나. */
	let pendingPatch = null;
	let saving = false;
	/** 저장이 다 끝나기를 기다리는 쪽(게시 등). */
	let idleWaiters = [];

	/**
	 * 지금 **글자를 치는 중**인가. 그렇다면 포커스가 빠질 때까지 미룬다.
	 * ⚠️ range(슬라이더)도 여기 넣는다 — 끄는 동안 input 이 수십 번 오는데, 놓아도 포커스가 남아 있어
	 *    다른 데를 누를 때 한 번 나간다. checkbox·radio·file 은 한 번에 끝나는 조작이라 바로 보낸다.
	 * ⚠️ `<select>` 는 INPUT 이 아니라 여기 안 걸린다 → 바로 보낸다(고르는 순간 끝난다).
	 */
	const isTyping = () => {
		const el = typeof document !== "undefined" ? document.activeElement : null;
		if (!el) return false;
		if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
		if (el.tagName !== "INPUT") return false;
		return !["checkbox", "radio", "button", "submit", "reset", "file", "color", "image"].includes(
			(el.type || "text").toLowerCase(),
		);
	};

	/** 기다리는 쪽을 전부 풀어 준다. `ok` = 쌓인 게 다 저장됐나. */
	const releaseWaiters = (ok) => {
		const waiters = idleWaiters;
		idleWaiters = [];
		waiters.forEach((resolve) => resolve(ok));
	};

	/**
	 * 쌓인 걸 보낸다. 보내는 중이면 끝난 뒤 이어서 보낸다(한 줄 세우기).
	 *
	 * ⚠️⚠️ **PRConnect 의 실패 콜백을 믿지 않는다.** 응답 자체가 없는 네트워크 오류에서는 그 catch 가
	 *    `err.response.data` 에서 다시 터져 **실패 콜백이 아예 안 불린다**(ui-lib PRConnect.send).
	 *    한 줄 세우기에서 그러면 `saving` 이 영영 true 로 남아 **그 뒤 저장이 전부 멈춘다** →
	 *    시간 제한(watchdog)을 같이 걸고, 어느 쪽이 먼저 오든 **한 번만** 정리한다(settle).
	 * ⚠️ 실패하면 **자동으로 다시 보내지 않는다** — 서버가 계속 거절하면 무한 반복이 된다.
	 *    실패한 칸은 되돌려 놓고, 다음 계기(포커스 빠짐·다른 조작·게시)에 다시 나간다.
	 */
	const pump = () => {
		if (saving) return;
		if (!pendingPatch) return releaseWaiters(true);
		const patch = pendingPatch;
		pendingPatch = null;
		saving = true;
		saveStatus = "saving";
		errorMessage = "";

		let settled = false;
		const settle = (data) => {
			if (settled) return;
			settled = true;
			clearTimeout(watchdog);
			saving = false;
			if (data?.header?.status !== "success") {
				// 그새 더 새 값이 쌓였으면 그쪽이 이긴다(실패한 옛 값이 덮지 않게 뒤에 펼친다).
				pendingPatch = { ...patch, ...(pendingPatch || {}) };
				saveStatus = "idle";
				errorMessage = "저장하지 못했습니다. 잠시 후 다시 시도해주세요.";
				releaseWaiters(false);
				return;
			}
			// 보내는 동안 또 쌓였으면 '저장됨'이 아니다 — 이어서 나갈 것이 남아 있다.
			saveStatus = pendingPatch ? (isTyping() ? "pending" : "saving") : "saved";
			// 서버가 응답 header 에 실어보낸 최신 날짜로 '미반영 변경' 점을 갱신한다.
			if (data.header.lastUpdateDate) {
				lastUpdateDate = data.header.lastUpdateDate;
				lastPostDate = data.header.lastPostDate ?? lastPostDate;
			}
			// 프리뷰에 **설정만 밀어 넣는다** — iframe 을 다시 로드하지 않아 깜빡임이 없고
			// 보던 화면(focus)도 유지된다. [처음부터 다시] 버튼은 그대로 remount 한다.
			pushConfig();
			// 글자를 치는 중에 쌓인 건 포커스가 빠질 때 나간다(여기서 바로 보내면 타이핑 중 저장이 된다).
			// ⚠️ 기다리는 쪽(게시)은 **풀지 않는다** — 아직 안 보낸 게 남아 있다. 다음 focusout 의 pump 가 푼다.
			if (pendingPatch && isTyping()) return;
			pump();
		};
		const watchdog = setTimeout(() => settle(null), 20000);
		PRConnect.send("/api/Bot/modifyLauncherSettings", { botId, ...patch }, settle, settle);
	};

	/**
	 * 쌓인 걸 **지금** 보내고, 다 끝나면 풀리는 약속을 준다(게시 직전·탭 이동).
	 * 풀릴 때 `true` = 다 저장됨 / `false` = 실패해서 남아 있음(게시를 멈춰야 한다).
	 */
	const flushSaves = () =>
		new Promise((resolve) => {
			if (!saving && !pendingPatch) return resolve(true);
			idleWaiters.push(resolve);
			pump();
		});

	/** 탭이 만진 칸만 모은다(부분 저장). 보내는 때는 위 주석. */
	const save = (patch) => {
		pendingPatch = { ...(pendingPatch || {}), ...patch };
		errorMessage = "";
		if (isTyping()) {
			if (!saving) saveStatus = "pending";
			return; // focusout 이 보낸다
		}
		pump();
	};

	// 글자 칸에서 포커스가 빠지면 보낸다. ⚠️ document 에 **캡처**로 건다 — 그림 고르기 모달처럼
	// 빌더 밖에 그려지는 칸도 있고, 탭이 바뀌며 칸이 사라지는 경우도 이 한 곳에서 받는다.
	const onFocusOut = () => {
		if (pendingPatch) pump();
	};
	// 창을 닫으려는데 안 보낸 게 있으면 보내 보고 **붙잡는다**(닫히면 요청이 잘릴 수 있다).
	const onBeforeUnload = (e) => {
		if (!pendingPatch && !saving) return;
		pump();
		e.preventDefault();
		e.returnValue = "";
	};
	onDestroy(() => {
		// 빌더를 떠나도(다른 화면으로 이동) 쌓인 건 보낸다 — 페이지는 살아 있어 요청은 끝까지 간다.
		if (pendingPatch) pump();
	});

	/**
	 * ⭐ 저작 중인 화면을 **클릭·포커스만 해도** 우측 프리뷰가 그 화면으로 바뀐다.
	 * 드롭다운으로 매번 고르게 하면 만드는 흐름이 끊긴다 — 만지는 곳이 곧 보는 곳이어야 한다.
	 */
	/**
	 * 저장된 설정을 프리뷰에 **밀어 넣는다**(iframe 을 다시 로드하지 않는다).
	 *
	 * ⚠️ 예전엔 저장마다 `previewKey` 를 올려 iframe 을 remount 했다 — 타이핑 한 글자마다
	 *    프리뷰가 새로 로드돼 깜빡이고, 보던 화면(focus)도 매번 다시 잡아야 했다.
	 *    프리뷰는 어차피 설정을 메시지로 받으므로 다시 로드할 이유가 없다.
	 * ⚠️ focus 를 함께 실어 **보던 화면이 유지**되게 한다.
	 */
	const pushConfig = () => {
		if (!previewWindow) return;
		PRConnect.send("/api/Bot/getLauncherConfig", { botId }, (data) => {
			if (data.header.status !== "success") return;
			previewWindow?.postMessage(
				{ type: "launcher-preview-config", config: data.result, focus: focusOf(previewFocus) },
				window.location.origin,
			);
		});
	};

	const onScreenFocus = (e) => {
		const d = e.detail;
		console.log("[launcher:builder] 화면 클릭/포커스", d);
		// ⚠️ 'home' → "" 로 눕힌다(위 focusOf 주석) — 메인 화면은 '가리킨 게 없는' 상태다.
		if (d?.kind === "home") return setPreviewFocus("");
		setPreviewFocus(d?.kind === "onboarding" ? `ob:${d.index}` : d?.kind || "");
	};

	/**
	 * [레시피] 탭 결과 전 화면을 만지는 중. ⭐ 연출을 바꾸면(`replay`) 그 값으로 처음부터 다시 돈다.
	 *   "now"  — [다시 보기]. 저장이 따라오지 않으니 **지금** 보낸다.
	 *   "next" — 연출 값을 바꿨다. 값만 올려 두면 **뒤따르는 저장의 pushConfig 가 새 설정과 함께** 싣고
	 *            간다. 여기서 바로 보내면 옛 설정으로 한 번 돌고, 새 설정은 같은 nonce 라 다시 안 돈다.
	 */
	const onRecipeFocus = (e) => {
		const { recipeId, replay, kind, popup, step, state, home } = e.detail || {};
		if (!recipeId) return;
		// 이벤트의 '메인 화면 툴팁' 칸 — 툴팁은 메인 카드 위에 뜬다 → 메인 화면(빈 값)을 보여준다.
		if (home) return setPreviewFocus("");
		// 레시피 단계(st:) / 경품 이벤트(evt:, 팝업·버튼 상태까지 가리킬 수 있다)
		const evtTail = state ? `:${popup || ""}:${state}` : popup ? `:${popup}` : "";
		const v =
			kind === "event"
				? `evt:${recipeId}${evtTail}`
				: kind === "done"
					? `done:${recipeId}${e.detail?.all ? ":all" : ""}`
					: `st:${recipeId}:${step || "intro"}`;
		if (replay) previewNonce += 1;
		if (v !== previewFocus) return setPreviewFocus(v);
		if (replay === "now") sendFocus();
	};

	/**
	 * 다른 탭으로 가면 홈으로 되돌린다 — [타일] 탭에서 온보딩이 떠 있으면 뭘 보는지 헷갈린다.
	 * ⚠️⚠️ **탭이 바뀔 때만** 본다. 예전엔 `currentTab !== "screen" && previewFocus` 를 반응문으로
	 *    두어 focus 가 바뀔 때마다 다시 돌았다 → [화면] 밖에서는 미리보기 드롭다운으로 화면을 골라도
	 *    **바로 홈으로 되돌아갔다**(고를 수는 있는데 안 먹는 셀렉트). 레시피 탭이 자기 화면을 가리키게
	 *    되면서 그 반응문이 레시피 focus 까지 지우게 돼 드러났다.
	 * ⚠️ `$: onTabChange(currentTab)` — 인자로만 넘겨야 의존성이 탭 하나로 좁혀진다.
	 */
	let lastTab = "";
	const onTabChange = (tab) => {
		if (tab === lastTab) return;
		lastTab = tab;
		// 탭을 옮기면 쌓인 저장을 보낸다(보통은 탭을 누를 때 포커스가 빠져 이미 나갔다 — 안전망).
		if (pendingPatch) pump();
		if (previewFocus && tabOfFocus(previewFocus) !== tab) setPreviewFocus("");
	};
	$: onTabChange(currentTab);
	// 가리키던 레시피가 **지워지면** 홈으로 — 드롭다운에서도 빠지는 값이라 그대로 두면 셀렉트가 빈칸으로 뜬다.
	$: if (
		previewFocus.startsWith("st:") &&
		!(settings.recipes || []).some((r) => `st:${r?.id}` === eventFocusBase(previewFocus) && r?.kind !== "event")
	)
		setPreviewFocus("");
	$: if (
		previewFocus.startsWith("done:") &&
		!(settings.recipes || []).some((r) => r?.id === previewFocus.split(":")[1] && r?.kind !== "event")
	)
		setPreviewFocus("");
	// 가리키던 경품 이벤트가 지워지면 홈으로(같은 이유).
	$: if (
		previewFocus.startsWith("evt:") &&
		!(settings.recipes || []).some((r) => `evt:${r?.id}` === eventFocusBase(previewFocus) && r?.kind === "event")
	)
		setPreviewFocus("");

	const onShellChange = (e) => {
		settings.shell = e.detail;
		save({ shell: settings.shell });
	};
	const onThemeChange = (e) => {
		settings.theme = e.detail;
		save({ theme: settings.theme });
	};
	const onTilesChange = (e) => {
		settings.tiles = e.detail;
		save({ tiles: settings.tiles });
	};
	const onIdentityChange = (e) => {
		settings.identity = e.detail;
		save({ identity: settings.identity });
	};
	const onRecipesChange = (e) => {
		settings.recipes = e.detail;
		save({ recipes: settings.recipes });
	};
	const onEconomyChange = (e) => {
		settings.economy = e.detail;
		save({ economy: settings.economy });
	};

	/**
	 * 공유 카드 저장. ⚠️ 다른 탭과 **저장소가 다르다**(BOTS 한 행) → 전용 엔드포인트로 나간다.
	 * ⚠️ 게임 빌더처럼 modifyBotStatisticsData 를 쓰면 안 된다 — 그 statement 가 DESIGNATE_URL
	 *    (=런처 주소)까지 덮어써 PAGE_URL 과 어긋나고 카카오 로그인이 조용히 깨진다.
	 */
	const onShareChange = (e) => {
		bot = { ...bot, ...e.detail };
		saveStatus = "saving";
		errorMessage = "";
		PRConnect.send(
			"/api/Bot/modifyLauncherShare",
			{
				botId,
				thumbnailImageContent: bot.thumbnailImageContent ?? null,
				thumbnailImageTitle: bot.thumbnailImageTitle ?? null,
				thumbnailImageSubTitle: bot.thumbnailImageSubTitle ?? null,
			},
			(data) => {
				if (data.header.status !== "success") {
					saveStatus = "idle";
					errorMessage = "저장하지 못했습니다. 잠시 후 다시 시도해주세요.";
					return;
				}
				saveStatus = "saved";
				// 서버가 touchBotDate 를 부르므로 최신 날짜가 응답 header 에 실려 온다
				// → 게시 대기 점이 바로 뜬다(공유 카드는 게시해야 공개 주소에 반영된다).
				if (data.header.lastUpdateDate) {
					lastUpdateDate = data.header.lastUpdateDate;
					lastPostDate = data.header.lastPostDate ?? lastPostDate;
				}
				// ⚠️ 여기만 remount 가 맞다 — og 메타는 설정이 아니라 **셸 HTML 에 구워진다**.
				//    메시지로 밀어 넣을 수 있는 값이 아니라 셸을 다시 받아야 한다.
				previewKey += 1;
			},
		);
	};

	// 게시는 레이아웃이 소유한다 — 여기선 열기 요청만 올린다.
	// ⚠️⚠️ **쌓인 저장을 먼저 끝낸다.** 저장을 포커스가 빠질 때로 미뤘으니, 게시 버튼을 누르는 순간
	//    (focusout → 저장 요청)과 게시 요청이 거의 동시에 나간다 → 게시가 먼저 처리되면 **방금 친 글자가
	//    빠진 채로** 게시된다. 한 번도 게시 안 한 런처는 팝업 없이 바로 게시되니 더 위험하다.
	// ⚠️ 저장이 실패했으면 게시를 멈춘다(에러 칩이 이미 떠 있다) — 옛 값으로 게시되면 더 헷갈린다.
	const publish = async () => {
		const ok = await flushSaves();
		if (!ok) return;
		messaging.set({
			target: "LauncherBuilderLayout",
			action: "openPublish",
			isPosted: !!lastPostDate,
		});
	};

	/**
	 * 프리뷰 iframe 이 "설정 주세요" 하면 **빌더가 대신 받아** 넘긴다.
	 *
	 * ⚠️ iframe 이 직접 /api/Bot/getLauncherConfig 를 부를 수 없다 — 그 API 는
	 *    `Authorization: Bearer` **헤더**로 인증하는데(쿠키가 아니다) iframe 의 fetch 에는
	 *    그 헤더가 없어서 NOT_AUTHORIZED 가 난다. 토큰을 iframe 에 넘기면 새는 길이 생기고,
	 *    공개 엔드포인트로 빼면 미공개 저작 설정이 botId 만 알면 보인다.
	 * ⚠️ 저장 직후에도 최신이어야 하므로 **그때그때 다시 받아서** 보낸다(캐시하지 않는다).
	 * ⚠️ 메시지 이름은 런타임 LAUNCHER/src/config.ts 의 PREVIEW_READY/PREVIEW_CONFIG 와 같아야 한다.
	 */
	const onPreviewMessage = (event) => {
		if (event.origin !== window.location.origin) return;
		if (event.data?.type !== "launcher-preview-ready") return;
		const target = event.source;
		// 프리뷰가 뜰 때마다 창이 바뀐다(새로고침·캐시키 변경) → 그때그때 잡아 둔다.
		previewWindow = target;
		console.log("[launcher:builder] 프리뷰 창 잡음(ready 수신)", { origin: event.origin });
		PRConnect.send("/api/Bot/getLauncherConfig", { botId }, (data) => {
			const config = data.header.status === "success" ? data.result : null;
			// ⚠️⚠️ focus 를 **설정과 같은 메시지에** 실어 보낸다. 따로 보내면 런타임이 아직 App 을
			//    마운트하지 않아 focus 리스너가 없고, 그 메시지는 **조용히 버려진다**
			//    (실제로 그래서 프리뷰 화면이 안 바뀌었다). 이후 변경만 별도 메시지로 간다.
			target?.postMessage(
				{ type: "launcher-preview-config", config, focus: focusOf(previewFocus) },
				window.location.origin,
			);
		});
	};

	/**
	 * ⚠️⚠️ 캐시 키가 **번들 버전 + 세션 카운터** 두 조각인 이유는 GameBuilder.previewSrc 주석과 같다 —
	 *    셸이 브라우저에 고이면 번들을 새로 배포해도 옛 번들에 갇힌다. previewKey 만으로는
	 *    세션 사이를 못 깬다(페이지를 새로 열면 0 으로 돌아간다).
	 */
	$: previewSrc =
		botId && bot.charId && shellVersion
			? `/static/avatalk/${botId}/${bot.charId}/index.html?v=${shellVersion}-${previewKey}`
			: "";
</script>

{#if isLoaded}
	<div class="builder-shell" class:creepong={planStore.isCreepong()}>
		<div
			class="tw-mx-auto tw-flex tw-h-full tw-w-full tw-overflow-hidden tw-bg-background tw-font-sans tw-text-foreground"
			style="max-width: var(--app-max-w, 1920px)"
		>
			<LauncherBuilderSidebar {tabList} {currentTab} on:change={(e) => (currentTab = e.detail)} />

			<div class="tw-flex tw-min-w-0 tw-flex-1 tw-justify-center tw-overflow-hidden tw-bg-background">
				<div class="tw-flex tw-w-full tw-min-w-0">
					<!-- 에디터: 메인 작업영역 -->
					<main class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
						<!-- 폰(<768px): 좌측 사이드바 대신 상단 가로 탭 -->
						<LauncherBuilderTopTabs {tabList} {currentTab} on:change={(e) => (currentTab = e.detail)} />

						<div class="tw-flex-1 tw-overflow-y-auto">
							<!-- 스티키 액션 툴바: 스크롤/모바일에서도 상단에 고정 → 게시·저장 상태가 항상 보인다. -->
							<div class="tw-sticky tw-top-0 tw-z-20 tw-border-b tw-border-border tw-bg-background/90 tw-backdrop-blur">
								<div class="tw-mx-auto tw-flex tw-w-full tw-max-w-[860px] tw-items-center tw-justify-between tw-gap-3 tw-px-6 tw-py-2.5">
									<div class="tw-min-w-0">
										<div class="tw-hidden tw-truncate tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground sm:tw-block">
											{bot.title || "런처"} · 런처페이지
										</div>
										<h1 class="tw-truncate tw-text-base tw-font-semibold tw-text-foreground sm:tw-text-lg">
											{currentTabLabel}
										</h1>
									</div>
									<div class="tw-relative tw-flex tw-shrink-0 tw-items-center tw-gap-2 sm:tw-gap-3">
										<!-- 자동 저장 상태(pill). 글자 칸은 **포커스가 빠질 때**, 그 밖의 조작은 바로 저장 → 버튼 대신 상태만. -->
										{#if errorMessage}
											<div
												class="tw-inline-flex tw-select-none tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-destructive/30 tw-bg-destructive/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-destructive"
												aria-live="polite"
											>
												{errorMessage}
											</div>
										{:else}
											<div
												class="tw-inline-flex tw-select-none tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-transition-colors {saveStatus ===
												'saving'
													? 'tw-border-border tw-bg-muted tw-text-muted-foreground'
													: saveStatus === 'saved'
														? 'tw-border-emerald-500/30 tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-400'
														: 'tw-border-transparent tw-bg-muted/50 tw-text-muted-foreground/70'}"
												aria-live="polite"
											>
												{#if saveStatus === "pending"}
													<!-- 치는 중 — 아직 안 보냈다. 칸을 벗어나면 저장된다. -->
													<span class="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-amber-500"></span>
													<span title="입력 칸을 벗어나면 저장됩니다">입력 중</span>
												{:else if saveStatus === "saving"}
													<svg class="save-spinner tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
													<span>저장 중…</span>
												{:else}
													<svg class="tw-h-3.5 tw-w-3.5 {saveStatus === 'saved' ? '' : 'tw-opacity-70'}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
													<span>저장됨</span>
												{/if}
											</div>
										{/if}
										<button
											type="button"
											class="tw-inline-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-transition-colors hover:tw-bg-primary/90"
											on:click={publish}
										>
											{#if hasUnpublished}
												<span class="publish-dot tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-foreground" title="게시되지 않은 변경사항이 있어요"></span>
											{/if}
											게시하기
										</button>
									</div>
								</div>
							</div>

							<div class="tw-mx-auto tw-w-full tw-max-w-[860px] tw-px-6 tw-pb-6 tw-pt-5">
								<div class="tw-space-y-4">
									{#if currentTab === "design"}
										<ShellContext
											{bot}
											shell={settings.shell}
											theme={settings.theme}
											bind:urls={shellUrls}
											on:shell={onShellChange}
											on:theme={onThemeChange}
										/>
									{:else if currentTab === "screen"}
										<ScreenContext
											{bot}
											shell={settings.shell}
											bind:urls={shellUrls}
											bind:screenUrls
											profileFields={settings.identity?.profileFields || []}
											activeKey={previewFocus}
											on:change={onShellChange}
											on:focus={onScreenFocus}
										/>
									{:else if currentTab === "tile"}
										<TileContext
											{bot}
											tiles={settings.tiles}
											bind:iconUrls
											currencyLabel={settings.economy?.currencyLabel || ""}
											recipes={settings.recipes}
											profileFields={settings.identity?.profileFields || []}
											collectProfile={settings.identity?.collectProfile !== false}
											on:change={onTilesChange}
										/>
									{:else if currentTab === "recipe"}
										<RecipeContext
											{bot}
											recipes={settings.recipes}
											tiles={settings.tiles}
											bind:recipeUrls
											profileFields={settings.identity?.profileFields || []}
											themeText={settings.theme?.colors?.text || ""}
											themeSurface={settings.theme?.colors?.surface || ""}
											on:change={onRecipesChange}
											on:focus={onRecipeFocus}
										/>
									{:else if currentTab === "identity"}
										<IdentityContext bind:bot identity={settings.identity} on:change={onIdentityChange} />
									{:else if currentTab === "economy"}
										<EconomyContext
											{bot}
											economy={settings.economy}
											bind:currencyUrl
											on:change={onEconomyChange}
										/>
									{:else if currentTab === "share"}
										<ShareContext {bot} on:change={onShareChange} />
									{:else if currentTab === "ops"}
										<OpsReportContext {bot} recipes={settings.recipes} />
									{/if}
								</div>
							</div>
						</div>
					</main>

					<!-- 좁은 화면: 프리뷰 닫혀 있을 때 떠 있는 열기 버튼 -->
					{#if isNarrow && !previewOpen && !opsTab}
						<button
							type="button"
							class="tw-fixed tw-bottom-5 tw-right-5 tw-z-[998] tw-inline-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary tw-px-4 tw-text-sm tw-font-medium tw-text-primary-foreground tw-shadow-lg tw-transition-colors hover:tw-bg-primary/90"
							on:click={() => (previewOpen = true)}
							title="미리보기 열기">🔎 미리보기</button
						>
					{/if}

					<!-- 좁은 화면 드로어 배경(탭하면 닫힘) -->
					{#if isNarrow && previewOpen && !opsTab}
						<button
							type="button"
							class="tw-fixed tw-inset-0 tw-z-[999] tw-cursor-default tw-bg-black/40"
							on:click={() => (previewOpen = false)}
							aria-label="미리보기 닫기"
						></button>
					{/if}

					<!-- 미리보기: 넓은 화면=사이드 / 좁은 화면(≤1023px)=오른쪽 슬라이드 드로어 -->
					<!-- ⚠️ [운영 지표] 탭에선 접는다(class:tw-hidden) — 그릴 저작이 없고, 넓어진 자리가 캡처에 낫다.
					     DOM 은 남겨 둔다: 지우면 iframe 이 다시 떠서 탭을 오갈 때마다 미리보기가 처음부터 시작한다. -->
					<aside
						class:tw-hidden={opsTab}
						class="tw-flex tw-flex-col tw-bg-background {isNarrow
							? 'tw-fixed tw-inset-y-0 tw-right-0 tw-z-[1000] tw-w-[min(440px,100vw)] tw-border-l tw-border-border tw-shadow-2xl tw-transition-transform ' +
								(previewOpen ? 'tw-translate-x-0' : 'tw-translate-x-full')
							: 'tw-w-[460px] tw-shrink-0 tw-border-l tw-border-border'}"
						aria-hidden={isNarrow && !previewOpen}
					>
						<div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-p-4">
							<div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-border tw-bg-card tw-shadow-sm">
								<header class="tw-flex tw-min-h-[52px] tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-border-border tw-px-4">
									<h2 class="tw-shrink-0 tw-text-lg tw-font-semibold tw-text-foreground">미리보기</h2>
									<!-- ⭐ 저작 중인 화면을 바로 띄운다. 온보딩·로그인은 조건이 맞아야 뜨는 화면이라
									     이게 없으면 만드는 걸 볼 수가 없다. -->
									<Select
										class="tw-ml-2 tw-h-8 tw-min-w-0 tw-flex-1 tw-text-xs"
										value={focusSelectValue}
										on:change={(e) => setPreviewFocus(e.target.value)}
									>
										{#each focusChoices as c (c.value)}
											<option value={c.value}>{c.label}</option>
										{/each}
									</Select>
									<div class="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
										<button
											type="button"
											class="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
											on:click={() => (previewKey += 1)}
											aria-label="처음부터 다시"
											title="처음부터 다시"
										>
											<svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
										</button>
										{#if isNarrow}
											<button
												type="button"
												class="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
												on:click={() => (previewOpen = false)}
												aria-label="미리보기 닫기"
												title="미리보기 닫기"
											>
												<svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
											</button>
										{/if}
									</div>
								</header>

								<!-- 런처는 390×844 모바일 화면이다(피그마 기준). 그 비율로 잡아야 실제와 같게 보인다. -->
								<div class="tw-flex tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center tw-p-3">
									<div class="launcher-preview-frame">
										{#if previewSrc}
											{#key previewKey}
												<iframe src={previewSrc} title="런처 미리보기" class="tw-block tw-h-full tw-w-full tw-border-0"></iframe>
											{/key}
										{/if}
									</div>
								</div>

								<footer class="tw-shrink-0 tw-border-t tw-border-border tw-px-4 tw-py-2.5 tw-text-xs tw-text-muted-foreground">
									저장하면 미리보기가 새로 시작됩니다.
								</footer>
							</div>
						</div>
					</aside>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	/* ChatBuilder·GameBuilder 와 같은 규격: 상단 고정 탑바(56px) 아래에 앉는다. */
	.builder-shell {
		margin-top: 56px;
		height: calc(100vh - 56px);
		width: 100%;
		overflow: hidden;
		background: hsl(var(--sb-background));
	}

	/* 크리퐁: 상단 네비바 없음 → 전체 높이 사용 */
	.builder-shell.creepong {
		margin-top: 0;
		height: 100vh;
	}

	/* 런처 화면 비율(390:844 ≒ 9:19.5). 높이가 모자라면 높이에 맞추고 폭이 줄어든다.
	 *
	 * ⚠️⚠️ **실제 기기보다 크게 키우지 않는다.** `height: 100%` 만 두면 패널 높이만큼 늘어나서
	 *    1080p 에서 약 916px(실기기 844)로 **8% 확대**돼 보인다 — 저작자가 글자·버튼·여백을
	 *    그만큼 크게 보고 판단하게 되니, 프리뷰로서 틀린 크기다. 남아도 844 에서 멈춘다.
	 *    (ChatBuilder `.preview-interface` 는 아예 `width: 375px` 로 못 박아 같은 결론에 와 있다 —
	 *     거기는 폭 기준, 여기는 높이 기준이라 방식만 다르다)
	 * ⚠️ 줄이는 쪽은 `height` 로 둬야 폭이 비율로 따라온다. 반대로 `width` 를 고정하고
	 *    `max-height` 를 걸면 **폭이 안 줄어** 비율이 깨진다(세로만 눌린 화면이 된다).
	 * ⚠️ GameBuilder `.game-preview-frame` 에 같은 문제가 그대로 있다 — 거기도 `height: 100%` 다.
	 */
	.launcher-preview-frame {
		aspect-ratio: 390 / 844;
		height: min(100%, 844px);
		max-width: 100%;
		border-radius: 14px;
		overflow: hidden;
		background: #f5efe6;
	}

	@keyframes save-spin {
		to {
			transform: rotate(360deg);
		}
	}

	.save-spinner {
		animation: save-spin 0.8s linear infinite;
	}

	/* 게시 대기(미반영 변경) 점: 은은한 맥동으로 시선을 끈다(봇/챗/게임 빌더와 동일) */
	@keyframes sb-pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.35;
		}
	}

	.publish-dot {
		animation: sb-pulse 1.6s ease-in-out infinite;
	}
</style>
